"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import SupportChat from "@/components/SupportChat";

// Lazy-load non-critical client UI that doesn't affect LCP.
// SupportChat stays statically imported so the launcher can paint
// immediately, while the heavier chat interactions still load on demand.
const StatusBanner = dynamic(() => import("@/components/StatusBanner"), { ssr: false });
const ConsoleEasterEgg = dynamic(
  () => import("@/components/ConsoleEasterEgg").then((m) => ({ default: m.ConsoleEasterEgg })),
  { ssr: false }
);
const CookieConsent = dynamic(() => import("@/components/CookieConsent"), { ssr: false });

const DASHBOARD_PREFIXES = [
  "/dashboard",
  "/agents",
  "/conversations",
  "/analytics",
  "/settings",
  "/billing",
  "/knowledge",
  "/integrations",
  "/templates",
  "/deploy",
  "/leads",
  "/help-center",
];

/**
 * Client-only shell that wraps non-critical interactive components.
 * Imported once in the root layout. Each component inside is lazy-loaded
 * via next/dynamic with ssr:false — they load after the main page content
 * has rendered and hydrated, reducing TBT and initial JS parse cost.
 */
export default function ClientShell() {
  const pathname = usePathname();
  const isDashboardRoute = DASHBOARD_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const isContactRoute = pathname === "/contact";
  const isWidgetRoute = pathname.startsWith("/widget/");
  const [cookieConsentReady, setCookieConsentReady] = useState(false);
  const [consoleReady, setConsoleReady] = useState(false);

  useEffect(() => {
    const cookieTimer = window.setTimeout(() => setCookieConsentReady(true), 2500);
    const consoleTimer = window.setTimeout(() => setConsoleReady(true), 20000);

    return () => {
      window.clearTimeout(cookieTimer);
      window.clearTimeout(consoleTimer);
    };
  }, []);

  // Render the launcher as soon as the client shell mounts on normal
  // public/dashboard routes. The chat component itself is still lazy-
  // loaded, but the button should not wait on timers or interaction.
  const supportChatReady = !isContactRoute && !isWidgetRoute;
  const cookieConsentEnabled = !isWidgetRoute;

  return (
    <>
      {isDashboardRoute ? <StatusBanner /> : null}
      {supportChatReady ? <SupportChat /> : null}
      {cookieConsentEnabled && cookieConsentReady ? <CookieConsent /> : null}
      {consoleReady ? <ConsoleEasterEgg /> : null}
    </>
  );
}
